import type { TFunction } from 'i18next';

import { EGoogleAdsKeywordMatchType } from '@feathr/blackbox';

import type { IAdGroupKeyword } from '../AdStep.types';

export interface IMatchTypeOption {
  id: EGoogleAdsKeywordMatchType;
  name: string;
}

/**
 * The messages for the keywords in one editor, each prefixed with the keyword it is about.
 *
 * A chip has nowhere to render a message, so they are collected and listed above the
 * chips instead. Errors arrive keyed for the whole ad group — both editors share one list
 * on the model — so this filters down to the keywords actually being rendered.
 *
 * @param keywords - The keywords this editor shows.
 * @param errors - Messages for the whole ad group, keyed by keyword key.
 */
export function getKeywordMessages(
  keywords: readonly IAdGroupKeyword[],
  errors: Record<string, string[]>,
): string[] {
  return keywords.flatMap((keyword) =>
    (errors[keyword.key] ?? []).map((message) => `${keyword.text}: ${message}`),
  );
}

/**
 * Human-readable label for a keyword match type. Kept out of the component so the
 * chip and the select can't drift apart.
 */
export function getMatchTypeLabel(matchType: EGoogleAdsKeywordMatchType, t: TFunction): string {
  const labels: Record<EGoogleAdsKeywordMatchType, string> = {
    [EGoogleAdsKeywordMatchType.BROAD]: t('Broad'),
    [EGoogleAdsKeywordMatchType.PHRASE]: t('Phrase'),
    [EGoogleAdsKeywordMatchType.EXACT]: t('Exact'),
  };
  return labels[matchType];
}

/**
 * Match type options for `UniversalSelect`. Its default option getters read `name`
 * then `id`, so no custom getters are needed.
 */
export function getMatchTypeOptions(t: TFunction): IMatchTypeOption[] {
  return Object.values(EGoogleAdsKeywordMatchType).map((matchType) => ({
    id: matchType,
    name: getMatchTypeLabel(matchType, t),
  }));
}
